import React from 'react';

import PageLayout from 'components/common/PageLayout';
import {
  ANALYTICS_ROUTE,
  API_ROUTE,
  ENTERPRISE_CONTACT_LINK,
  INTEGRATIONS_ROUTE,
  SECURITY_ROUTE,
} from 'components/constants';

import styles from './DataAndPrivacy.module.scss';

const signalGroups = [
  {
    number: '01',
    title: 'Declared data',
    text: 'Capture answers, preferences, contact details and other information people choose to provide.',
    items: ['Form fields', 'Answers and choices', 'Preferences and intent'],
  },
  {
    number: '02',
    title: 'Journey behavior',
    text: 'Understand how people move through the experience, not only what appears in the final submission.',
    items: ['Views and navigation', 'Interactions and clicks', 'Completion and drop-off'],
  },
  {
    number: '03',
    title: 'Experience context',
    text: 'Keep each signal connected to the product, card, path and session in which it happened.',
    items: ['Product and card context', 'Flow path and outcome', 'Device and platform'],
  },
  {
    number: '04',
    title: 'Business outcomes',
    text: 'Connect the journey to the action your team cares about and pass that result to the next system.',
    items: ['Lead submission', 'Score or result', 'Selected next action'],
  },
];

const dataPaths = [
  {
    number: '01',
    eyebrow: 'Use the Storycards dashboard',
    title: 'Review and manage collected records in one place.',
    text:
      'Give approved team members access to product-level responses, leads and performance data. Keep day-to-day review close to the product that collected it.',
  },
  {
    number: '02',
    eyebrow: 'Export when your team needs it',
    title: 'Move records and reports into an existing workflow.',
    text:
      'Download the audience data or analytics report your team needs for deeper analysis, operational review or stakeholder reporting.',
  },
  {
    number: '03',
    eyebrow: 'Connect your own systems',
    title: 'Send data to the stack your organization controls.',
    text:
      'Route selected responses and journey events to CRM, analytics, data and internal systems through integrations, webhooks or the Storycards API.',
  },
  {
    number: '04',
    eyebrow: 'Use Data Bridge',
    title: 'Transfer collected data without storing it in Storycards.',
    text:
      'Send collected data directly to your server or database when the workflow requires customer-controlled storage and handling.',
  },
];

const controls = [
  {
    title: 'Encryption',
    text: 'Audience data stored in Storycards is encrypted to protect it while it is managed in the platform.',
  },
  {
    title: 'Permission-based access',
    text: 'Limit who can view or export collected information according to the access granted inside your organization.',
  },
  {
    title: 'Deletion controls',
    text: 'Delete information for a product from the dashboard when your organization no longer needs it.',
  },
  {
    title: 'Customer-controlled destinations',
    text: 'Choose the CRM, database, server, analytics platform or internal system that should receive selected data.',
  },
];

const activationUseCases = [
  {
    title: 'Enrich customer profiles',
    text: 'Add declared preferences, needs and intent to the customer records your teams already use.',
  },
  {
    title: 'Route the right follow-up',
    text: 'Use responses, scores or completion outcomes to send a lead or request into the relevant business process.',
  },
  {
    title: 'Personalize the journey',
    text: 'Use information collected earlier in the flow to change the content, path or outcome shown next.',
  },
  {
    title: 'Measure in your data stack',
    text: 'Send interaction and completion events into the analytics and reporting environment used across the organization.',
  },
];

const insightViews = [
  {
    title: 'Product and card performance',
    text: 'Compare views, answers and engagement across complete products and individual cards.',
  },
  {
    title: 'Completion and drop-off',
    text: 'See how many people complete the experience and identify the step where they leave.',
  },
  {
    title: 'Journey paths',
    text: 'Understand how people move through branches, choices and outcomes across the full flow.',
  },
  {
    title: 'Device and regional context',
    text: 'Compare performance across mobile, desktop and tablet, and review engagement by region.',
  },
];

const DataAndPrivacy: React.FC = () => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>First-party data collection</p>
            <h1>Turn every interaction into data your organization can use.</h1>
            <p className={styles.heroText}>
              Collect answers, choices, intent and journey behavior inside interactive products people want to
              complete. Keep the data in Storycards, export it or send it to the systems your organization controls.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
              <a href={API_ROUTE} className={styles.textLink}>Explore the API</a>
            </div>
          </div>

          <div className={styles.heroSummary}>
            <span>One collection layer</span>
            <div>
              <strong>Useful by design</strong>
              <p>Capture declared data and behavioral signals as part of the same connected journey.</p>
            </div>
            <div>
              <strong>Controlled by your organization</strong>
              <p>Choose who can access the data, where it is sent and when stored records are deleted.</p>
            </div>
          </div>
        </div>

        <figure className={styles.dataMap}>
          <figcaption>
            <span>First-party data flow</span>
            <em><i /> Configurable by product</em>
          </figcaption>
          <div className={styles.mapCanvas}>
            <div className={styles.mapNode}>
              <span>Interactive experience</span>
              <strong>People engage and respond</strong>
              <ul>
                <li>Answers</li>
                <li>Choices</li>
                <li>Journey events</li>
              </ul>
            </div>
            <div className={styles.mapConnector} aria-hidden="true">
              <span>Structured events</span>
              <i />
            </div>
            <div className={`${styles.mapNode} ${styles.controlNode}`}>
              <span>Storycards data controls</span>
              <strong>Choose the route for each product</strong>
              <div className={styles.routeList}>
                <span>Manage</span><span>Export</span><span>Transfer</span>
              </div>
            </div>
            <div className={styles.mapConnector} aria-hidden="true">
              <span>Your rules</span>
              <i />
            </div>
            <div className={styles.destinationNode}>
              <span>Your approved destination</span>
              <div className={styles.destinationGrid}>
                <strong>CRM</strong>
                <strong>Analytics</strong>
                <strong>Database</strong>
                <strong>Internal systems</strong>
              </div>
            </div>
          </div>
        </figure>
      </section>

      <section className={styles.statement}>
        <div className={styles.sectionInner}>
          <h2>Data collection is part of the experience, not a form at the end.</h2>
          <div>
            <p>
              Storycards combines content, questions, forms and logic in one connected flow. Each response can shape
              what the person sees next while creating structured first-party data for your team.
            </p>
            <p>
              The result is richer than a final submission. You can understand what people told you, how they moved
              through the journey, where they left and which outcome they reached.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.signalsSection}>
        <div className={styles.sectionInner}>
          <header className={styles.signalsHeader}>
            <div>
              <p className={styles.eyebrow}>A complete interaction record</p>
              <h2>Collect the answer and the context around it.</h2>
            </div>
            <p>
              Keep declared information connected to the journey signals that explain what happened before, during
              and after each response.
            </p>
          </header>
          <div className={styles.signalGrid}>
            {signalGroups.map((group) => (
              <article key={group.title}>
                <span>{group.number}</span>
                <h3>{group.title}</h3>
                <p>{group.text}</p>
                <ul>{group.items.map((item) => <li key={item}>{item}</li>)}</ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.pathsSection}>
        <div className={styles.sectionInner}>
          <header className={styles.pathsHeader}>
            <p className={styles.eyebrow}>Flexible data architecture</p>
            <h2>Choose where the data goes.</h2>
            <p>
              Use Storycards as the working environment, move data into another workflow or send it directly to your
              own infrastructure. The route can match the needs of each product.
            </p>
          </header>
          <div className={styles.pathList}>
            {dataPaths.map((path) => (
              <article key={path.title}>
                <span>{path.number}</span>
                <div>
                  <small>{path.eyebrow}</small>
                  <h3>{path.title}</h3>
                  <p>{path.text}</p>
                </div>
              </article>
            ))}
          </div>
          <div className={styles.pathsLinks}>
            <a href={INTEGRATIONS_ROUTE}>Explore integrations <span aria-hidden="true">→</span></a>
            <a href={API_ROUTE}>Explore the API <span aria-hidden="true">→</span></a>
          </div>
        </div>
      </section>

      <section className={styles.controlSection}>
        <div className={styles.sectionInner}>
          <div className={styles.controlIntro}>
            <p className={styles.eyebrow}>Data ownership and control</p>
            <h2>Keep control from collection to destination.</h2>
            <p>
              Data controls sit alongside the product workflow, so teams can operate the experience without losing
              sight of access, storage, transfer and deletion requirements.
            </p>
            <a href={SECURITY_ROUTE} className={styles.textLink}>Review Storycards security</a>
          </div>
          <div className={styles.controlGrid}>
            {controls.map((control, index) => (
              <article key={control.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{control.title}</h3>
                <p>{control.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.activationSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>From data to action</p>
              <h2>Put each signal to work in the next step.</h2>
            </div>
            <p>
              Storycards supplies structured data from the experience. Your teams and systems decide how it should be
              used across customer, operational and reporting workflows.
            </p>
          </header>
          <div className={styles.activationGrid}>
            {activationUseCases.map((useCase, index) => (
              <article key={useCase.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{useCase.title}</h3>
                <p>{useCase.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.insightsSection}>
        <div className={styles.sectionInner}>
          <div className={styles.insightsLayout}>
            <div className={styles.insightsCopy}>
              <p className={styles.eyebrow}>Built for continuous improvement</p>
              <h2>See what people do, where they leave and what to improve.</h2>
              <p>
                Review performance at product, card and journey level. Use the same interaction data to understand
                audience behavior and make focused changes to content, design or flow.
              </p>
              <a href={ANALYTICS_ROUTE} className={styles.insightsLink}>
                Explore analytics and optimization <span aria-hidden="true">→</span>
              </a>
            </div>
            <div className={styles.insightGrid}>
              {insightViews.map((view) => (
                <article key={view.title}>
                  <h3>{view.title}</h3>
                  <p>{view.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Design the experience and the data flow together.</h2>
          <div>
            <p>
              Talk with our enterprise team about what you need to collect, where the data should go and how your
              teams will use it.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default DataAndPrivacy;
